var App = App || {};
App.Initialization = App.Initialization || {};

App.Initialization['component.formValidation'] = (function () {
    'use strict';

    // each initialized form will have its own state stored in this WeakMap, keyed by the form element
    const formStates = new WeakMap();

    // Initializes form validation for a given form
    const initialize = form => {
        formLoad(form);
        formUpdateSubmit(form);
    };

    // Loads form and attaches input validation listeners
    const formLoad = form => {
        const formInputs = form.querySelectorAll("[data-zag-validate-input]");
        formInputs.forEach(input => {
            // the server points aria-describedby at the (hidden) error container; screen readers still read
            // hidden content referenced that way, so only reference it while an error is actually shown
            setErrorDescription(input, false);
            input.addEventListener("input", () => validateInput(input));
        });

        // tie server-side (Kentico) errors returned after a submit to their controls
        form.querySelectorAll("[data-zag-validate-field]").forEach((field, i) => {
            const serverErrors = Array.from(field.querySelectorAll(".field-validation-error")).filter(el => el.textContent.trim() !== "");
            if (!serverErrors.length) return;

            const ids = serverErrors.map((el, j) => el.id || (el.id = `${form.id || "form"}_server-error_${i}_${j}`));
            field.querySelectorAll("[data-zag-validate-input]").forEach(control => {
                const describedBy = (control.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
                control.setAttribute("aria-describedby", [...describedBy, ...ids].join(" "));
                control.setAttribute("aria-invalid", "true");
                control.classList.add("is-invalid");
            });
        });
    };

    // Once the user edits a field, client-side validation takes over from any server-side error message
    const clearServerErrors = field => {
        const serverErrors = Array.from(field.querySelectorAll(".field-validation-error"));
        if (!serverErrors.length) return;

        const ids = serverErrors.map(el => el.id).filter(Boolean);
        serverErrors.forEach(el => {
            el.textContent = "";
            el.classList.replace("field-validation-error", "field-validation-valid");
        });
        field.querySelectorAll("[data-zag-validate-input]").forEach(control => {
            const describedBy = (control.getAttribute("aria-describedby") || "").split(/\s+/).filter(id => id && !ids.includes(id));
            if (describedBy.length) control.setAttribute("aria-describedby", describedBy.join(" "));
            else control.removeAttribute("aria-describedby");
        });
    };

    // Adds/removes the field's error container id from an input's aria-describedby (WCAG 3.3.1, 4.1.2)
    const setErrorDescription = (input, hasError) => {
        const field = input.closest("[data-zag-validate-field]");
        const errorsId = field?.querySelector("[data-zag-validate-errors]")?.id;
        if (!errorsId) return;

        const ids = (input.getAttribute("aria-describedby") || "").split(/\s+/).filter(id => id && id !== errorsId);
        if (hasError) ids.push(errorsId);

        if (ids.length) input.setAttribute("aria-describedby", ids.join(" "));
        else input.removeAttribute("aria-describedby");
    };

    // Keeps aria-invalid, Bootstrap's is-invalid and aria-describedby in sync with the visible error state of every control in a field
    const setFieldInvalid = (field, hasError) => {
        field.querySelectorAll("[data-zag-validate-input]").forEach(control => {
            if (hasError) control.setAttribute("aria-invalid", "true");
            else control.removeAttribute("aria-invalid");
            control.classList.toggle("is-invalid", hasError);
            setErrorDescription(control, hasError);
        });
    };

    // Updates form submit behavior to use custom validation
    const formUpdateSubmit = form => {

        // store the Kentico ajax-update target; removing it stops Kentico re-rendering the form (and our error
        // state) on every change/blur. Kentico's onsubmit stays in place: its invisible reCAPTCHA rewrites that
        // attribute once grecaptcha loads (after this runs), so removing it broke the reCAPTCHA challenge.
        const kenticoUpdate = form.getAttribute("data-ktc-ajax-update") || "";
        formStates.set(form, { kenticoUpdate, allowSubmit: false });
        form.removeAttribute("data-ktc-ajax-update");

        // capture on the parent so validation runs before the form's own onsubmit (Kentico / reCAPTCHA) handler;
        // Kentico replaces the form after a submit, but the parent stays, so check the event is for this form
        const listenerTarget = form.parentElement || document;
        listenerTarget.addEventListener("submit", e => {
            if (e.target === form) formCustomSubmit(e, form);
        }, true);
    };

    // Handles custom form submission with validation
    const formCustomSubmit = (e, form) => {
        const formState = formStates.get(form);
        if (!formState) return;

        // validation already passed: let this submit through to Kentico's onsubmit (reCAPTCHA challenge / ajax submit)
        if (formState.allowSubmit) {
            formState.allowSubmit = false;
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        // mark form as submitted
        form.setAttribute("data-zag-validate-form", "submitted");

        // validate each input
        const inputs = form.querySelectorAll("[data-zag-validate-input]");
        inputs.forEach(input => validateInput(input));

        // check for errors
        const errors = form.querySelectorAll('[data-zag-validate-field="error"]');
        if (errors.length > 0) {
            // move focus to the first invalid control so its label and error (aria-describedby) are announced;
            // the reCAPTCHA container is not focusable, so focus its iframe instead
            const firstErrorInput = errors[0]?.querySelector("[data-zag-validate-input]");
            const focusTarget = firstErrorInput?.dataset.zagValidateInput === "recaptcha"
                ? (firstErrorInput.querySelector("iframe") || firstErrorInput)
                : firstErrorInput;
            focusTarget?.focus();
            return;
        }

        // restore Kentico's ajax-update target so its submit handler swaps the response in place
        const isAjaxForm = Boolean(formState.kenticoUpdate);
        if (isAjaxForm) {
            form.setAttribute("data-ktc-ajax-update", formState.kenticoUpdate);

            // scroll to the top of the form (potentially for a confirmation message)
            const formTop = form.getBoundingClientRect().top + window.pageYOffset - 65;
            const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            window.scrollTo({ top: formTop, behavior: reduceMotion ? "auto" : "smooth" });
        }

        // requestSubmit() is a no-op while this submit event is still being dispatched, so resubmit on the next task
        formState.allowSubmit = true;
        setTimeout(() => {
            submitForm(form);

            // disable submit button to prevent multiple submissions (after submitting, so it isn't left out of the request)
            if (isAjaxForm) {
                const submitButton = form.querySelector("input[type='submit'], button[type='submit']");
                if (submitButton) submitButton.disabled = true;
            }
        }, 0);
    };

    // Submits through a real submit event so Kentico's onsubmit handler (reCAPTCHA challenge, then its AJAX submit)
    // runs: it swaps in the confirmation message or server errors in place, where Form.js moves focus / announces
    // them (WCAG 2.4.3, 3.3.1, 4.1.3). form.submit() skips submit events and would post the browser straight to the
    // form-builder endpoint without a reCAPTCHA token.
    const submitForm = form => {
        if (typeof form.requestSubmit === "function") {
            form.requestSubmit();
            return;
        }

        // fallback for browsers without requestSubmit: inline onsubmit handlers still run for dispatched events
        const proceed = form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
        if (proceed) form.submit();
    };

    // Validates a single input field
    const validateInput = input => {
        // find the field container
        const field = input.closest("[data-zag-validate-field]");
        if (!field) return;

        clearServerErrors(field);

        // get all error containers
        const errorsContainers = field.querySelectorAll("[data-zag-validate-errors]");
        errorsContainers.forEach(el => el.style.display = "block"); // make visible for validation

        // helper functions
        const show = el => el.style.display = "flex";
        const hide = el => el.style.display = "none";

        // get all error messages in this field
        const errorMessages = field.querySelectorAll("[data-zag-validate-error]");

        // required error
        const requiredErrors = Array.from(errorMessages).filter(el => el.dataset.zagValidateError === "required");

        const inputType = input.dataset.zagValidateInput;

        if (inputType === "text") {
            requiredErrors.forEach(err => {
                input.value.trim() !== "" ? hide(err) : show(err);
            });
        }
        else if (inputType === "upload") {
            // Kentico records a completed upload in a hidden field and keeps it when the form is re-rendered, which
            // leaves the new file input empty; the file input still counts while a just-picked file is uploading
            const tempFile = field.querySelector('input[type="hidden"][name$=".TempFileIdentifier"]');
            const hasFile = input.value !== "" || Boolean(tempFile && tempFile.value !== "");
            requiredErrors.forEach(err => hasFile ? hide(err) : show(err));
        }
        else if (inputType === "check") {
            const anyChecked = field.querySelectorAll('input[data-zag-validate-input="check"]:checked').length > 0;
            requiredErrors.forEach(err => anyChecked ? hide(err) : show(err));
        }
        else if (inputType === "recaptcha") {
            const recaptcha = field.querySelector(".g-recaptcha-response");
            const recaptchaResponse = recaptcha ? recaptcha.value : "";
            requiredErrors.forEach(err => recaptchaResponse !== "" ? hide(err) : show(err));
        }

        // maximum length
        const maxErrors = Array.from(errorMessages).filter(el => el.dataset.zagValidateError === "maximum");
        maxErrors.forEach(err => {
            const maxVal = parseMaxMin(err.dataset.zagValidateErrorValue);
            input.value.length <= maxVal ? hide(err) : show(err);
        });

        // like Kentico's server-side validation, length and pattern rules only apply to a value that has been entered
        const isEmpty = input.value === "";

        // minimum length
        const minErrors = Array.from(errorMessages).filter(el => el.dataset.zagValidateError === "minimum");
        minErrors.forEach(err => {
            const minVal = parseMaxMin(err.dataset.zagValidateErrorValue);
            isEmpty || input.value.length >= minVal ? hide(err) : show(err);
        });

        // regex validation
        const regexErrors = Array.from(errorMessages).filter(el => el.dataset.zagValidateError === "regex");
        regexErrors.forEach(err => {
            const pattern = new RegExp(err.dataset.zagValidateErrorValue);
            isEmpty || pattern.test(input.value) ? hide(err) : show(err);
        });

        // count number of visible errors in all error containers
        const visibleErrors = Array.from(errorMessages).filter(err => err.offsetParent !== null); // visible check
        if (visibleErrors.length > 0) {
            field.dataset.zagValidateField = "error";
            errorsContainers.forEach(el => el.style.display = "block");
            setFieldInvalid(field, true);
        }
        else {
            field.dataset.zagValidateField = "";
            errorsContainers.forEach(el => el.style.display = "none");
            setFieldInvalid(field, false);
        }
    };

    // Parses max/min value from string
    const parseMaxMin = str => {
        const num = parseFloat(str);
        return isNaN(num) ? 0 : num;
    };

    return {
        initialize
    };

})();